import { capitalize, uniq } from 'lodash'
import {
  ContentFileUploadType,
  IContentFileUploadImage,
  IDynamicPaperFulfilledWithLessons,
  IDynamicPaperQuestion,
  IDynamicPaperTopicFulfilled,
  IDynamicPaperTopicFulfilledWithLessons,
  IDynamicPaperTopicsGroupedByPaper,
  IExamResource,
  IGptConversationMessage,
  IGptResponseArguments,
  MessageRole,
  TContentFileUpload,
  THistoryMessageOpenText,
} from '../types'
import { extractMathpixImageLinks } from './mathpix'
import { IDpLesson } from '../types/firebase/dp-lesson'

export interface IDynamicPaperCounts {
  questions: number
  totalMarks: number
  earnedMarks: number
}

/**
 * ES-03: one source for question and mark counts. Computed from the paper's
 * fulfilled lessons - the same rows the paper sidebar
 * (DynamicPapersExplorerTree) renders - so the header, the landing page and
 * the stored questionsCount / totalMarks fields all agree with the sidebar.
 */
export function getDynamicPaperCounts(
  lessons: IDpLesson[],
): IDynamicPaperCounts {
  return {
    questions: lessons.length,
    totalMarks: lessons.reduce((sum, lesson) => sum + (lesson.totalMarks || 0), 0),
    earnedMarks: lessons.reduce(
      (sum, lesson) => sum + (lesson.earnedMarks ?? 0),
      0,
    ),
  }
}
import { questionFormatFullTextSwitchObj } from './exam-questions'

/**
 * Extracts the total marks from a list of dynamic paper topics.
 *
 * @param topics - An array of fulfilled dynamic paper topics, each containing questions with mark values.
 * @returns The total number of marks calculated by summing the marks of all questions in all topics.
 */
export function extractTotalMarksFromDynamicPaperTopicQuestions(
  topics: IDynamicPaperTopicFulfilled[],
): number {
  return topics.reduce((total, topic) => {
    const topicMarks = topic.questions.reduce((sum, question) => {
      const marks = Number(question.marks)
      return isNaN(marks) ? sum : sum + marks
    }, 0)

    return total + topicMarks
  }, 0)
}

/**
 * Converts each word in a topic name to start case (capitalizes the first letter of each word).
 *
 * @param topicName - A string representing the topic name (e.g. "math and science").
 * @returns The topic name with each word capitalized (e.g. "Math And Science").
 */
export function startCaseDynamicPaperTopicName(topicName: string) {
  return topicName.replace(/\w+/g, capitalize)
}

/**
 * Groups and sorts dynamic paper questions by their primary question number.
 *
 * @param questions - An array of exam questions to be grouped.
 * @returns An array of grouped and sorted questions, where each group contains a primary question number
 *          and its corresponding sorted sub-questions (question parts).
 *
 * The function performs:
 * 1. Grouping by primary question number (from questionNo).
 * 2. Sorting groups by numeric order of primary question numbers.
 * 3. Sorting each group's question parts by their original question numbers.
 */
export function groupDynamicPaperQuestions(
  questions: IExamResource[],
): IDynamicPaperQuestion[] {
  const questionMap = new Map<string, IDynamicPaperQuestion>()

  // First pass: group questions by primary number
  questions.forEach((question) => {
    const { primaryNumber } = getPrimaryQuestionNumber(question.questionNo)

    if (!questionMap.has(primaryNumber)) {
      questionMap.set(primaryNumber, {
        primaryQuestionNumber: primaryNumber,
        questionParts: [],
      })
    }

    questionMap.get(primaryNumber)!.questionParts.push(question)
  })

  // Second pass: sort groups and parts
  return Array.from(questionMap.values())
    .sort((a, b) =>
      questionNumberNumericSort(
        a.primaryQuestionNumber,
        b.primaryQuestionNumber,
      ),
    )
    .map((group) => ({
      ...group,
      questionParts: group.questionParts.sort((a, b) =>
        questionNumberNumericSort(a.questionNo, b.questionNo),
      ),
    }))
}

/**
 * Extracts the primary question number from a question string.
 *
 * For example:
 * - "1a" → "1"
 * - "2bii" → "2"
 * - "3" → "3"
 *
 * @param questionNo - The full question number string (e.g., "1a", "2bi", "3").
 * @returns An object with the `primaryNumber` field containing the leading numeric portion,
 *          or the full string if no numeric prefix is found.
 */
export function getPrimaryQuestionNumber(questionNo: string): {
  primaryNumber: string
} {
  // Handle cases like: 1, 1a, 1ai, 1ai-iii
  const match = questionNo.match(/^(\d+)/) // Extract leading numbers
  return {
    primaryNumber: match ? match[1] : questionNo, // Fallback to full string if no numbers
  }
}

const ROMAN_NUMERAL_MAP: Record<string, number> = {
  i: 1,
  ii: 2,
  iii: 3,
  iv: 4,
  v: 5,
  vi: 6,
  vii: 7,
  viii: 8,
  ix: 9,
  x: 10,
  xi: 11,
  xii: 12,
  xiii: 13,
  xiv: 14,
  xv: 15,
  xvi: 16,
  xvii: 17,
  xviii: 18,
  xix: 19,
  xx: 20,
  xxi: 21,
  xxii: 22,
  xxiii: 23,
  xxiv: 24,
  xxv: 25,
  xxvi: 26,
  xxvii: 27,
  xxviii: 28,
  xxix: 29,
  xxx: 30,
}

function romanToNumber(roman: string): number {
  return (
    ROMAN_NUMERAL_MAP[roman.toLowerCase()] ?? roman.toLowerCase().charCodeAt(0)
  )
}

/**
 * Tokenizes a question number string into its constituent parts for sorting.
 *
 * Breaks down complex question numbers into an array of numbers and strings
 * that can be compared sequentially. Handles formats like:
 * - Simple numbers: "16" → [16]
 * - Letter suffixes: "1a" → [1, "a"]
 * - Roman numeral subparts: "2b(iii)" → [2, "b", 3]
 * - Ranges: "1c(i-x)" → [1, "c", 1] (uses start of range)
 * - Standalone roman numerals: "xi" → [11]
 *
 * @param {string} question - The question number string to tokenize
 * @returns {(string|number)[]} Array of tokens representing the question number components
 *
 * @example
 * tokenize("1a")        // → [1, "a"]
 * tokenize("2b(iii)")   // → [2, "b", 3]
 * tokenize("3c(i-x)")   // → [3, "c", 1]
 * tokenize("xi")        // → [11]
 * tokenize("16")        // → [16]
 * tokenize("Appendix")  // → ["appendix"] (fallback)
 */
function tokenize(question: string): (string | number)[] {
  const cleaned = question.toLowerCase().replace(/\s+/g, '')

  // Extract parts like: 2a(ii), 2c(i-x), 1b(iii), 19(i-x), xi, xii
  const match = cleaned.match(/^(\d+)([a-z])?(?:\(([^)]+)\))?$|^([a-z]+)$/)

  if (!match) return [cleaned] // fallback: treat whole string as one token

  const [, numberPart, letterPart, subPart, romanOnly] = match

  const tokens: (string | number)[] = []

  if (numberPart) {
    tokens.push(parseInt(numberPart, 10))
    if (letterPart) tokens.push(letterPart)
    if (subPart) {
      if (subPart.includes('-')) {
        const [start] = subPart.split('-')
        tokens.push(romanToNumber(start))
      } else {
        tokens.push(romanToNumber(subPart))
      }
    }
  } else if (romanOnly) {
    tokens.push(romanToNumber(romanOnly))
  }

  return tokens.length ? tokens : [cleaned]
}

/**
 * Numerically sorts two question number strings in natural human-readable order.
 * Handles complex question numbering formats including:
 * - Numeric prefixes (1, 2, 10)
 * - Letter suffixes (a, b, c)
 * - Roman numeral subparts (i, ii, iii, iv)
 * - Range notations (i-x)
 * - Mixed formats (1a, 2b(iii), 3c(i-x))
 *
 * @param {string} a - First question number to compare
 * @param {string} b - Second question number to compare
 * @returns {number} - Negative if a comes first, positive if b comes first, 0 if equal
 *
 * @example
 * // Basic numeric sorting
 * questionNumberNumericSort('1', '2') // → -1
 *
 * @example
 * // Letter suffix sorting
 * questionNumberNumericSort('1a', '1b') // → -1
 *
 * @example
 * // Roman numeral subparts
 * questionNumberNumericSort('1a(i)', '1a(ii)') // → -1
 *
 * @example
 * // Ranges (sorted by start value)
 * questionNumberNumericSort('1a(i-iv)', '1a(ii-v)') // → -1
 *
 * @example
 * // Mixed length comparison
 * questionNumberNumericSort('1a', '1a(i)') // → -1
 */
export function questionNumberNumericSort(a: string, b: string): number {
  const aTokens = tokenize(a)
  const bTokens = tokenize(b)

  for (let i = 0; i < Math.max(aTokens.length, bTokens.length); i++) {
    const aToken = aTokens[i]
    const bToken = bTokens[i]

    // Shorter comes first if otherwise equal
    if (aToken === undefined) return -1
    if (bToken === undefined) return 1

    // Compare numbers numerically
    if (typeof aToken === 'number' && typeof bToken === 'number') {
      if (aToken !== bToken) return aToken - bToken
    }
    // Mixed types or strings - compare as strings
    else {
      const aStr = String(aToken)
      const bStr = String(bToken)

      // Try numeric comparison first (for numbers in strings)
      const numA = parseInt(aStr, 10)
      const numB = parseInt(bStr, 10)
      if (!isNaN(numA) && !isNaN(numB)) {
        if (numA !== numB) return numA - numB
      }

      // Fallback to localeCompare
      const cmp = aStr.localeCompare(bStr, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
      if (cmp !== 0) return cmp
    }
  }

  return 0
}

export function groupDynamicPaperTopicsByPaperName(
  dynamicPaperFulfilled: IDynamicPaperFulfilledWithLessons,
): IDynamicPaperTopicsGroupedByPaper[] {
  // Create a map to group topics by paperName
  const paperNameMap = new Map<
    string,
    IDynamicPaperTopicFulfilledWithLessons[]
  >()

  // Iterate through each lesson and its topic and group them by paperName
  dynamicPaperFulfilled.lessons.forEach((lesson) => {
    if (lesson.topicDoc) {
      let existingTopics = paperNameMap.get(lesson.topicDoc.paperName) || []

      const existingTopic = existingTopics.find(
        (t) => t.id === lesson.topicDoc?.id,
      )

      if (existingTopic) {
        existingTopic.lessons.push(lesson)
        existingTopics = existingTopics.map((t) =>
          t.id === lesson.topicId ? existingTopic : t,
        )
      } else {
        existingTopics.push({ ...lesson.topicDoc, lessons: [lesson] })
      }
      paperNameMap.set(lesson.topicDoc.paperName, existingTopics)
    }
  })

  // Convert the map to an array of objects with paperName and topics
  // Also merge topics that have the same topicName within each paper
  const result: IDynamicPaperTopicsGroupedByPaper[] = []
  paperNameMap.forEach((topics, paperName) => {
    // Merge topics with the same topicName
    const topicNameMap = new Map<
      string,
      IDynamicPaperTopicFulfilledWithLessons
    >()

    topics.forEach((topic) => {
      const normalizedName = topic.topicName.toLowerCase().trim()
      const existingTopic = topicNameMap.get(normalizedName)

      if (existingTopic) {
        // Merge lessons from this topic into the existing one
        // Also deduplicate lessons by their ID
        const existingLessonIds = new Set(
          existingTopic.lessons.map((l) => l.id),
        )
        const newLessons = topic.lessons.filter(
          (l) => !existingLessonIds.has(l.id),
        )
        existingTopic.lessons = [...existingTopic.lessons, ...newLessons]
      } else {
        // First occurrence of this topicName - add it (with a copy of lessons array)
        topicNameMap.set(normalizedName, {
          ...topic,
          lessons: [...topic.lessons],
        })
      }
    })

    result.push({
      paperName,
      topics: Array.from(topicNameMap.values()),
    })
  })

  return result
}

export function updateTargetLessonEarnedMarksInDynamicPaper(
  paper: IDynamicPaperFulfilledWithLessons,
  stats: {
    lessonId: string
    earnedMarks?: number
    wasAttempted?: boolean
  },
): IDynamicPaperFulfilledWithLessons {
  return {
    ...paper,
    ...(stats.earnedMarks ? { earnedMarks: stats.earnedMarks } : {}),
    lessons: (paper.lessons || []).map((l) =>
      l.id === stats.lessonId
        ? {
            ...l,
            ...(stats.earnedMarks ? { earnedMarks: stats.earnedMarks } : {}),
            completed: !!(
              l.totalMarks &&
              stats.earnedMarks &&
              l.totalMarks === stats.earnedMarks
            ),
            ...(stats.wasAttempted !== undefined && {
              wasAttempted: stats.wasAttempted,
            }),
          }
        : l,
    ),
  }
}

/**
 * Removes LaTeX display math square brackets (\[ ... \]) that wrap around
 * a tabular environment (\begin{tabular}...\end{tabular}) in the input string.
 *
 * This is useful for cleaning up GPT-generated LaTeX content that unnecessarily
 * wraps tables in display math mode.
 *
 * @param {string} latex - The LaTeX string potentially containing \[ \begin{tabular}...\end{tabular} \].
 * @returns {string} - The cleaned LaTeX string with square brackets removed around tabular blocks.
 *
 * @example
 * const input = "\\[\\begin{tabular}{|c|c|}\\hline A & B \\\\ \\hline\\end{tabular}\\]";
 * const output = removeSquareBracketsAroundTabular(input);
 * // output: "\\begin{tabular}{|c|c|}\\hline A & B \\\\ \\hline\\end{tabular}"
 */
export function removeSquareBracketsAroundTabular(latex: string): string {
  return latex.replace(
    /\\\[\s*(\\begin{tabular}[\s\S]*?\\end{tabular})\s*\\\]/g,
    (_match, tabularContent) => tabularContent,
  )
}

export function generateDynamicPapersGptConversation(
  messages: THistoryMessageOpenText[],
): IGptConversationMessage[] {
  let conversation: IGptConversationMessage[] = []

  for (const message of messages) {
    if (message.role === MessageRole.System) {
      const content: TContentFileUpload = []
      const textContent = message.content as string

      // add system message text
      content.push({
        type: ContentFileUploadType.Text,
        text: textContent,
      })

      // extract image links from question text
      let links = extractMathpixImageLinks(
        // use choiceData.question to options links not to be extracted from regular textContent (whole system message)
        message.choiceData ? message.choiceData.question : textContent,
      )
      links = uniq(links) // filter out duplicate images (variant.question vs. questiontextInput)

      for (const link of links) {
        const fileUploadContent: IContentFileUploadImage = {
          type: ContentFileUploadType.ImageUrl,
          image_url: { url: link },
        }
        content.push(fileUploadContent)
      }

      // add choice data options images
      if (message.choiceData) {
        let isOptionsHeaderSet = false
        // add choice data options images
        for (const choiceOption of message.choiceData.options) {
          const links = extractMathpixImageLinks(choiceOption.content)

          let isOptionLabelSet = false

          // push option images links
          for (const link of links) {
            // set header only if links found
            if (!isOptionsHeaderSet) {
              content.push({
                type: ContentFileUploadType.Text,
                text: `${
                  questionFormatFullTextSwitchObj[
                    message.choiceData.questionFormat
                  ]
                } Options:`,
              })
              isOptionsHeaderSet = true
            }

            if (!isOptionLabelSet) {
              // push option label only if links found
              content.push({
                type: ContentFileUploadType.Text,
                text: `Option ${choiceOption.id} (image${
                  links.length > 1 ? 's' : ''
                } below):`,
              })
              isOptionLabelSet = true
            }

            const fileUploadContent: IContentFileUploadImage = {
              type: ContentFileUploadType.ImageUrl,
              image_url: { url: link },
            }
            content.push(fileUploadContent)
          }
        }
      }

      conversation.push({ content, role: message.role })
    }

    if (message.role === MessageRole.Assistant) {
      // this case could be potentially reproduced when error text saved as assistant message
      if (typeof message.content === 'string' && !!message.content) {
        conversation.push({
          content: message.content,
          role: message.role,
        })

        continue
      }

      const {
        gptResponseToStudent,
        commonMistake,
        tip,
        marksBreakdown,
        actualMarks,
        totalMarks,
      } = message.content as IGptResponseArguments

      let textContent = gptResponseToStudent

      if (commonMistake) {
        textContent += `\n\nCommon Mistake:\n${commonMistake}`
      }

      if (tip) {
        textContent += `\n\nTip:\n${tip}`
      }

      if (actualMarks && totalMarks) {
        textContent += `\n\nMarks Awarded: ${actualMarks}/${totalMarks}`
      }

      if (marksBreakdown) {
        textContent += `\n\nMarks Breakdown:\n${marksBreakdown}`
      }

      // add message only if content is presented, empty content could lead to infinite next response errors
      if (textContent) {
        conversation.push({
          content: textContent,
          role: message.role,
        })
      }
    }

    if (message.role === MessageRole.User) {
      const textContent = message.content as string
      const content: TContentFileUpload = []

      // add regular text part if presented
      if (textContent) {
        content.push({
          type: ContentFileUploadType.Text,
          text: textContent,
        })
      }

      // add images content parts
      if (!!message.imageUrls?.length) {
        for (const url of message.imageUrls) {
          const fileUploadContent: IContentFileUploadImage = {
            type: ContentFileUploadType.ImageUrl,
            image_url: { url },
          }

          !!url && content.push(fileUploadContent)
        }
      }

      // add choices content image parts
      if (!!message.choiceSelection?.length) {
        for (const choiceOption of message.choiceSelection) {
          const links = extractMathpixImageLinks(choiceOption.content)

          for (const link of links) {
            const fileUploadContent: IContentFileUploadImage = {
              type: ContentFileUploadType.ImageUrl,
              image_url: { url: link },
            }

            !!link && content.push(fileUploadContent)
          }
        }
      }

      conversation.push({
        content,
        role: message.role,
      })
    }
  }

  return conversation
}
